/**
 * OrgAttendanceKpis — the single source of truth for the org-today
 * attendance KPI tiles.
 *
 * Before this component the same KPIs were duplicated as inline JSX in
 * TWO places that drifted apart:
 *   - dashboard/components/widgets/AttendanceSummary.tsx (4 tiles, read
 *     `present_today` correctly)
 *   - Attendance/AttendanceOverview/index.tsx (6 tiles, read `present`
 *     — a key the backend never emits, so the tile silently showed 0)
 *
 * Both now render this component. One fetch, one field-name map, one
 * i18n namespace (attendance:overview.kpiLabels.*). Field names are
 * pinned by the backend ASGI smoke
 * (tests/attendance/test_overview_kpis_smoke.py).
 *
 * Two visual variants keep each host's established look:
 *   - variant="compact"  → dashboard widget: 4 tiles, icon + number +
 *                          progress bar against the org headcount.
 *   - variant="full"     → overview page: 6 KPI tiles in a responsive
 *                          row (adds Early Leave + Pending Approvals).
 *
 * The `full` variant can hand its fetched payload back to the host via
 * `onData` so the Attendance Overview page renders its pending-approvals
 * and department-summary tables from the SAME single fetch instead of
 * calling the endpoint twice.
 */

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import {
  BeachAccessOutlined,
  CheckCircleOutlined,
  PersonOffOutlined,
  TimerOutlined,
  LogoutOutlined,
  PendingActionsOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, attendanceApi } from '../../api';
import { queryDashboard } from '../../api/dashboardQuery';
import type {
  AttendanceOverviewKpis,
  AttendanceOverviewResponse,
} from '../../api/attendance';
import { WidgetErrorState } from '../primitives';
import { translateApiError } from '../../i18n/translateApiError';

interface Tile {
  key: string;
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
  /** Path the tile navigates to on click. */
  path: string;
}

export interface OrgAttendanceKpisProps {
  variant?: 'compact' | 'full';
  /** Receives the raw payload so a host can reuse the single fetch. */
  onData?: (data: AttendanceOverviewResponse) => void;
}

const buildTiles = (
  kpis: AttendanceOverviewKpis,
  t: (k: string) => string,
  variant: 'compact' | 'full',
): Tile[] => {
  const base: Tile[] = [
    {
      key: 'present',
      label: t('attendance:overview.kpiLabels.present'),
      value: kpis.present_today,
      color: '#4CAF50',
      icon: <CheckCircleOutlined />,
      path: '/attendance/time-entries',
    },
    {
      key: 'absent',
      label: t('attendance:overview.kpiLabels.absent'),
      value: kpis.absent,
      color: '#F44336',
      icon: <PersonOffOutlined />,
      path: '/attendance/time-entries?filter=absent',
    },
    {
      key: 'late',
      label: t('attendance:overview.kpiLabels.late'),
      value: kpis.late,
      color: '#FF9800',
      icon: <TimerOutlined />,
      path: '/attendance/time-entries?filter=late',
    },
    {
      key: 'onLeave',
      label: t('attendance:overview.kpiLabels.onLeave'),
      value: kpis.on_leave,
      color: '#2196F3',
      icon: <BeachAccessOutlined />,
      path: '/attendance/leave/balances',
    },
  ];
  if (variant === 'full') {
    base.push(
      {
        key: 'earlyLeave',
        label: t('attendance:overview.kpiLabels.earlyLeave'),
        value: kpis.early_leave,
        color: '#FF7043',
        icon: <LogoutOutlined />,
        path: '/attendance/time-entries?filter=early_leave',
      },
      {
        key: 'pendingApprovals',
        label: t('attendance:overview.kpiLabels.pendingApprovals'),
        value: kpis.pending_approvals,
        color: '#9C27B0',
        icon: <PendingActionsOutlined />,
        path: '/attendance/approvals',
      },
    );
  }
  return base;
};

const OrgAttendanceKpis: React.FC<OrgAttendanceKpisProps> = ({
  variant = 'compact',
  onData,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState<AttendanceOverviewKpis | null>(null);
  const [headcount, setHeadcount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = variant === 'compact'
          ? await queryDashboard(
              'attendance-overview',
              () => attendanceApi.overviewApi.get(),
            )
          : await attendanceApi.overviewApi.get();
        if (cancelled) return;
        setKpis(raw.kpis);
        const total = (raw.department_summary ?? []).reduce(
          (sum, d) => sum + (d.total ?? 0),
          0,
        );
        setHeadcount(total);
        onData?.(raw);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof ApiError
              ? translateApiError(e, t)
              : t('attendance:overview.kpiLabels.loadError'),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // onData intentionally omitted: hosts pass a stable/inline callback
    // and we only want the fetch to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, variant]);

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', py: variant === 'full' ? 4 : 0 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !kpis) {
    return <WidgetErrorState error={error ?? 'No data'} />;
  }

  const tiles = buildTiles(kpis, t, variant);

  if (variant === 'full') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(6, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        {tiles.map((tile) => (
          <Paper
            key={tile.key}
            elevation={0}
            onClick={() => navigate(tile.path)}
            sx={{
              p: 1.5,
              minWidth: 0,
              minHeight: 88,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'border-color 120ms',
              '&:hover': { borderColor: tile.color },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
              <Box sx={{ color: tile.color, display: 'flex', mt: 0.1, '& .MuiSvgIcon-root': { fontSize: 16 } }}>
                {tile.icon}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  minHeight: '2.4em',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  lineHeight: 1.2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {tile.label}
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mt: 'auto', pt: 0.5, fontWeight: 700, color: tile.color, lineHeight: 1 }}>
              {tile.value}
            </Typography>
          </Paper>
        ))}
      </Box>
    );
  }

  // compact (dashboard widget): progress bars against org headcount.
  const denom =
    headcount ||
    kpis.present_today + kpis.absent + kpis.late + kpis.on_leave ||
    1;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, fontWeight: 500 }}>
        {t('attendance:overview.kpiLabels.todaySummary', {
          date: new Date().toLocaleDateString(i18n.language, { weekday: 'short', month: 'short', day: 'numeric' }),
        })}
      </Typography>
      <Grid container spacing={1}>
        {tiles.map((tile) => {
          const pct = Math.min(100, (tile.value / denom) * 100);
          return (
            <Grid item xs={12 / 4} key={tile.key}>
              <Paper
                elevation={0}
                onClick={() => navigate(tile.path)}
                sx={{
                  p: 1,
                  height: '100%',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  border: '1px solid transparent',
                  transition: 'border-color 120ms',
                  '&:hover': { borderColor: 'divider' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'background.paper',
                      '& .MuiSvgIcon-root': { color: tile.color, fontSize: '1.1rem' },
                    }}
                  >
                    {tile.icon}
                  </Box>
                  <Box sx={{ flex: 1, ml: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}>
                      {tile.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: tile.color }}>
                        {tile.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        / {denom}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    // Visible track (was background.paper = white-on-white, which
                    // made the bar read as a floating single colour with no
                    // proportion — owner feedback). A grey track turns it into a
                    // readable "X of {denom}" fill (e.g. 1/2 = half).
                    bgcolor: 'grey.200',
                    mt: 'auto',
                    '& .MuiLinearProgress-bar': { bgcolor: tile.color },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.62rem', mt: 0.25, alignSelf: 'flex-end' }}
                >
                  {Math.round(pct)}%
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default OrgAttendanceKpis;
